import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Search, Plane } from "lucide-react";
import api from "../../lib/api";
import { TimeSlotSelect } from "./TimeSlotSelect";
import { HandoverPicker } from "./HandoverPicker";
import { ErrorModal } from "./ErrorModal";
import { MonthlySuggestModal } from "./MonthlySuggestModal";

const slotToTime = (v) => `${String(Math.floor(v)).padStart(2, "0")}:${v % 1 ? "30" : "00"}`;

export const BookingWidget = ({ initialMode = "short_term" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [branches, setBranches] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const qs = new URLSearchParams(window.location.search);
  const [pickupDate, setPickupDate] = useState(() => qs.get("pickup")?.slice(0, 10) || dayjs().add(2, "hour").format("YYYY-MM-DD"));
  const [pickupTime, setPickupTime] = useState(() => {
    const t = qs.get("pickup")?.slice(11, 16);
    return t ? Number(t.slice(0, 2)) + (t.slice(3) === "30" ? 0.5 : 0) : null;
  });
  const [returnDate, setReturnDate] = useState(() => qs.get("return")?.slice(0, 10) || dayjs().add(2, "hour").add(3, "day").format("YYYY-MM-DD"));
  const [returnTime, setReturnTime] = useState(() => {
    const t = qs.get("return")?.slice(11, 16);
    return t ? Number(t.slice(0, 2)) + (t.slice(3) === "30" ? 0.5 : 0) : null;
  });
  const [handover, setHandover] = useState(() => ({
    type: qs.get("handover") === "delivery" ? "delivery" : "branch",
    branchId: qs.get("branch") || "DXB-T3",
    emirate: qs.get("emirate") || "dubai",
    useCurrentLocation: qs.get("cur") === "1",
    returnType: qs.get("return_type") || (qs.get("handover") === "delivery" ? "collect" : "branch"),
    returnBranchId: qs.get("return_branch") || qs.get("branch") || "DXB-T3",
    returnEmirate: qs.get("return_emirate") || qs.get("emirate") || "dubai",
    returnUseCurrentLocation: qs.get("return_cur") === "1",
  }));
  const [flight, setFlight] = useState(() => ({ number: qs.get("flight_no") || "", arrival: qs.get("flight_arr") || "" }));
  const [ageOk, setAgeOk] = useState(() => qs.get("age_ok") === "1");
  const [error, setError] = useState(null);
  const [suggestDays, setSuggestDays] = useState(null);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => setMode(initialMode), [initialMode]);

  if (!branches)
    return <div className="bg-white rounded-lg shadow-2xl p-6 h-40 animate-pulse" data-testid="booking-widget-loading" />;

  const station = branches.groups.flatMap((g) => g.stations).find((s) => s.id === handover.branchId);
  const isAirport = handover.type === "branch" && station?.type === "airport";

  const bumpPickupDate = () => {
    const today = dayjs().format("YYYY-MM-DD");
    if (pickupDate <= today) {
      const next = dayjs(pickupDate).add(1, "day").format("YYYY-MM-DD");
      setPickupDate(next);
      if (returnDate < next) setReturnDate(dayjs(next).add(3, "day").format("YYYY-MM-DD"));
    }
  };

  // Pick-up time drives the drop-off time (a 1-day rental is 24 hours).
  const handlePickupTime = (v) => { setPickupTime(v); setReturnTime(v); };

  const showClosed = (name) =>
    setError({
      title: t("This location is closed"),
      message: t("{{name}} isn't open at the time you chose. Please pick another time or branch, or choose delivery instead.", { name: name || t("This branch") }),
    });

  const buildParams = () => {
    const params = new URLSearchParams({
      mode,
      pickup: `${pickupDate}T${slotToTime(pickupTime ?? 10)}`,
      handover: handover.type,
      return_type: handover.returnType,
      age_ok: ageOk ? "1" : "0",
    });
    if (handover.type === "branch") {
      params.set("branch", handover.branchId);
    } else {
      params.set("emirate", handover.emirate);
      if (handover.useCurrentLocation) params.set("cur", "1");
    }
    if (handover.returnType === "branch") {
      params.set("return_branch", handover.returnBranchId);
    } else {
      params.set("return_emirate", handover.returnEmirate || handover.emirate);
      if (handover.returnUseCurrentLocation) params.set("return_cur", "1");
    }
    if (isAirport && flight.number.trim()) {
      params.set("flight_no", flight.number.trim().toUpperCase());
      if (flight.arrival) params.set("flight_arr", flight.arrival);
    }
    return params;
  };

  const submit = (e) => {
    e.preventDefault();
    if (pickupTime == null) {
      setError({ title: t("Choose a time"), message: t("No times are available on your pick-up date, so we've suggested the next day. Pick an available time to continue.") });
      return;
    }
    const pickupDt = dayjs(`${pickupDate}T${slotToTime(pickupTime ?? 10)}`);
    if (mode === "short_term") {
      const returnDt = dayjs(`${returnDate}T${slotToTime(returnTime ?? 10)}`);
      if (!returnDt.isAfter(pickupDt)) {
        setError({ title: t("Check your dates"), message: t("The drop-off must be after the pick-up. Please adjust your return date or time.") });
        return;
      }
      const days = Math.max(1, Math.ceil(returnDt.diff(pickupDt, "hour") / 24));
      if (days >= 22) {
        setSuggestDays(days);
        return;
      }
      const params = buildParams();
      params.set("return", `${returnDate}T${slotToTime(returnTime ?? 10)}`);
      navigate(`/results?${params.toString()}`);
    } else {
      navigate(`/monthly?${buildParams().toString()}`);
    }
  };

  const moveToMonthly = () => {
    const params = buildParams();
    params.set("mode", "monthly");
    params.set("auto", "1");
    setSuggestDays(null);
    navigate(`/monthly?${params.toString()}`);
  };

  const continueDaily = () => {
    const params = buildParams();
    params.set("return", `${returnDate}T${slotToTime(returnTime ?? 10)}`);
    params.set("force_daily", "1");
    setSuggestDays(null);
    navigate(`/results?${params.toString()}`);
  };

  return (
    <div className="bg-white rounded-lg shadow-2xl p-5 sm:p-6 w-full" data-testid="booking-widget">
      <div className="flex rounded-md overflow-hidden border border-[#d5ddd5] mb-5 w-fit">
        <button type="button" onClick={() => setMode("short_term")}
          className={`px-4 py-2.5 text-xs sm:text-sm font-medium uppercase tracking-wide transition-colors ${mode === "short_term" ? "bg-[#009900] text-white" : "bg-white text-[#144733] hover:bg-[#f0f5f0]"}`}
          data-testid="search-tab-short-term">{t("Short-Term Rental")}</button>
        <button type="button" onClick={() => setMode("monthly")}
          className={`px-4 py-2.5 text-xs sm:text-sm font-medium uppercase tracking-wide transition-colors ${mode === "monthly" ? "bg-[#009900] text-white" : "bg-white text-[#144733] hover:bg-[#f0f5f0]"}`}
          data-testid="search-tab-monthly">{t("Monthly Rental")}</button>
      </div>

      <form onSubmit={submit} className="space-y-4">
        {/* Step 1 — dates */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="field-label">{mode === "monthly" ? t("Start date *") : t("Pick-up date *")}</label>
            <input type="date" className="field-input" min={dayjs().format("YYYY-MM-DD")} value={pickupDate}
              onChange={(e) => {
                setPickupDate(e.target.value);
                if (dayjs(returnDate).isBefore(dayjs(e.target.value))) setReturnDate(dayjs(e.target.value).add(3, "day").format("YYYY-MM-DD"));
              }} data-testid="pickup-date-input" />
          </div>
          <div>
            <label className="field-label">{t("Time *")}</label>
            <TimeSlotSelect date={pickupDate} mode={handover.type === "branch" ? "branch" : "delivery"}
              stationId={handover.branchId} emirate={handover.emirate}
              value={pickupTime} onChange={handlePickupTime} onNoneAvailable={bumpPickupDate} onClosed={showClosed} testId="pickup-time-select" />
          </div>
          {mode === "short_term" && (
            <>
              <div>
                <label className="field-label">{t("Drop-off date *")}</label>
                <input type="date" className="field-input" min={pickupDate} value={returnDate}
                  onChange={(e) => setReturnDate(e.target.value)} data-testid="return-date-input" />
              </div>
              <div>
                <label className="field-label">{t("Time *")}</label>
                <TimeSlotSelect date={returnDate}
                  mode={handover.returnType === "branch" ? "branch" : "delivery"}
                  stationId={handover.returnType === "branch" ? handover.returnBranchId : handover.branchId}
                  emirate={handover.returnType === "collect" && handover.type === "branch" ? (handover.returnEmirate || handover.emirate) : handover.emirate}
                  value={returnTime} onChange={setReturnTime} onClosed={showClosed} testId="return-time-select" />
              </div>
            </>
          )}
        </div>

        {/* Step 2 — handover */}
        <HandoverPicker branches={branches} value={handover} onChange={setHandover} mode={mode} />

        {/* Step 2b — flight details (airports only) */}
        {isAirport && (
          <div className="bg-[#f4f7f4] border border-[#e2ebe4] rounded-lg p-3" data-testid="flight-details-section">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-[#144733] mb-2">
              <Plane className="w-4 h-4 text-[#009900]" /> {t("Flying in? (optional)")}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className="field-input" placeholder={t("Flight number, e.g. EK202")} value={flight.number}
                onChange={(e) => setFlight({ ...flight, number: e.target.value })} data-testid="flight-number-input" />
              <input type="time" className="field-input" value={flight.arrival}
                onChange={(e) => setFlight({ ...flight, arrival: e.target.value })} data-testid="flight-arrival-input" />
            </div>
            <p className="text-[11px] text-[#57685e] mt-2">{t("We track your flight, so your car is ready even if you land late, at no extra charge.")}</p>
          </div>
        )}

        {/* Step 3 — age qualifier */}
        <label className="flex items-start gap-2 text-sm text-[#0F1E17] cursor-pointer" data-testid="age-checkbox-label">
          <input type="checkbox" className="mt-0.5" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} data-testid="age-checkbox" />
          <span>{t("I am above the age of 21 and I am eligible to drive in the UAE")}</span>
        </label>

        <button type="submit" className="btn-cta rounded w-full py-3.5 text-sm flex items-center justify-center gap-2" data-testid="search-vehicles-btn">
          <Search className="w-4 h-4" /> {mode === "monthly" ? t("View monthly offers") : t("Search vehicles")}
        </button>
      </form>

      <ErrorModal error={error} onClose={() => setError(null)} />
      <MonthlySuggestModal open={!!suggestDays} days={suggestDays} onMoveToMonthly={moveToMonthly} onContinue={continueDaily} />
    </div>
  );
};
